import {Component, OnInit} from "@angular/core";
import {AddressService} from "../../address/address.service";
import {
  addressModelFormGroup,
  ItsmAddressModel,
  itsmAddressModelFactory,
  itsmCreatableAddressModelFactor,
} from "../../address/itsm-address.model";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {DialogComponent} from "../../bootstrap-modal/dialog.component";
import {FormGroup} from "@angular/forms";
import {exposeComponentInDebugMode} from "../../misc/expose-component-in-debug-mode.decorator";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {toggleLoading} from "../../misc/loading.decorator";
import {SearchHelpers} from "../../helpers/search-helper";
import {AppSettingsService} from "../../app-settings/app-settings.service";
import {shippingLabelPlaceholders} from "../../types/shipping-label-placeholders";
import {ProjectService} from "../../project/project.service";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 5;

export interface SelectItsmAddressDialogParamsInterface {
  projectId: string; // Required to make authentication easier
  allowCreate: boolean;
  requireBpId: boolean;
  selectedTmcAddressId: string;
  itsmSiteId: string;
  itsmSiteName: string;
  itsmAddresses: ItsmAddressModel[];
  addressService: AddressService;
  targetSystem: string;
  defaultAddress?: any;
  titleModifier?: string; // What address are we modifying
  site?: string;
  hideDeliveryType?: boolean;
  buildingType: string;
}

export interface SelectItsmAddressDialogResultInterface {
  selectedTmcAddressId: string;
  changed: boolean;
  createdAddress: ItsmAddressModel;
}

@Component({
  templateUrl: "./select-itsm-address-dialog.template.pug",
})
@exposeComponentInDebugMode()
export class SelectItsmAddressDialogComponent
  extends DialogComponent<SelectItsmAddressDialogParamsInterface, SelectItsmAddressDialogResultInterface>
  implements SelectItsmAddressDialogParamsInterface, OnInit {
  // Inputs
  projectId: string;
  allowCreate: boolean;
  requireBpId: boolean;
  selectedTmcAddressId: string;
  itsmSiteId: string;
  itsmSiteName: string;
  itsmAddresses: ItsmAddressModel[];
  addressService: AddressService;
  targetSystem: string;
  defaultAddress: any;
  titleModifier: string;
  site?: string;
  hideDeliveryType?: boolean = false;
  buildingType: string;

  // Other parameters
  originalTmcAddressId: string;
  preFilteredItsmAddresses: ItsmAddressModel[];
  filteredItsmAddresses: ItsmAddressModel[];
  displayedItsmAddresses: ItsmAddressModel[] = [];

  // Search parameters
  loading: boolean = false;
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";

  // Search expansion
  emptyAddress: ItsmAddressModel = itsmAddressModelFactory();
  addressDetail: ItsmAddressModel = itsmAddressModelFactory();
  expandedTmcAddressId: string = "";

  // Create parameters
  countryId: string;
  createSelected: boolean = false;
  addressForm: FormGroup = addressModelFormGroup();

  itsmBpIdMap: any = {};
  allowedBpId: boolean = false;
  itsmBpIdWarning: string;
  itsmBpIdInUse: boolean = false;
  validBpIdRegEx: RegExp = /^.*$/; // TODO remove

  itsmBuildingNameMap: any = {};
  allowedBuildingName: boolean = false;
  itsmBuildingNameWarning: string;
  itsmBuildingNameInUse: boolean = false;

  itsmMatchingAddressMap: any = {};
  itsmAddressInUse: boolean = false;
  itsmAddressInUseWarning: string;
  allowedAddress: boolean = false;

  toggleCustomLabelHelperVisible: boolean = false;
  placeholders: any[] = [];

  shippingLabelParameters: any = {
    customShippingLabel1: "customShippingLabel1",
    customShippingLabel2: "customShippingLabel2",
  };

  isRegionRequired: boolean = true;
  currentFocus: string = this.shippingLabelParameters.customShippingLabel1;

  // Error handling
  error: Error;

  deliveryTypes: any = [
    {key: "DOCK", humanReadable: "Dock delivery"},
    {key: "DESKTOP", humanReadable: "Desktop delivery"},
  ];

  buildingTypes: any = [
    {value: null, humanReadable: ""},
    {value: ["Service"], humanReadable: "Service"},
    {value: ["Consumables"], humanReadable: "Consumables"},
    {value: ["Service", "Consumables"], humanReadable: "Both"},
  ];

  // public enabledIndirectDataModel: boolean;

  static showAddressModal(
    dialogService: DialogService,
    model: SelectItsmAddressDialogParamsInterface,
  ): Promise<SelectItsmAddressDialogResultInterface> {
    return dialogService.showDialog(SelectItsmAddressDialogComponent, model);
  }

  checkBpId(businessPartnerId) {
    const trimmedBpId = (businessPartnerId || "").trim();
    if (businessPartnerId !== trimmedBpId) this.addressForm.controls["businessPartnerId"].setValue(trimmedBpId);
    this.itsmBpIdInUse = false;

    if (!trimmedBpId) {
      this.itsmBpIdWarning = "";
      this.allowedBpId = !this.requireBpId;
      return;
    }

    const searchBpId = trimmedBpId.toLowerCase();
    if (this.itsmBpIdMap[searchBpId]) {
      this.itsmBpIdWarning = "ERROR - BP ID is already in use at this customer";
      this.allowedBpId = false;
      this.itsmBpIdInUse = true;
      return;
    }

    if (!trimmedBpId.match(this.validBpIdRegEx)) {
      this.itsmBpIdWarning = "ERROR - Invalid BP ID";
      this.allowedBpId = false;
      return;
    }

    this.itsmBpIdWarning = "";
    this.allowedBpId = true;
  }

  checkBuildingName(buildName) {
    const trimmedBuildName = (buildName || "").trim();
    if (buildName !== trimmedBuildName) {
      this.addressForm.controls["itsmBuilding"].setValue(trimmedBuildName);
    }
    this.itsmBuildingNameInUse = false;

    if (!trimmedBuildName) {
      this.itsmBuildingNameWarning = "";
      this.allowedBuildingName = false;
      return;
    }

    const searchBuildName = trimmedBuildName.toLowerCase();
    if (this.itsmBuildingNameMap[searchBuildName]) {
      this.itsmBuildingNameWarning = "ERROR - Building name is already in use at this ITSM Site";
      this.allowedBuildingName = false;
      this.itsmBuildingNameInUse = true;
      return;
    }

    this.itsmBuildingNameWarning = "";
    this.allowedBuildingName = true;
  }

  checkPreExistingAddress(rawAddressFormValues) {
    this.itsmAddressInUse = false;

    const {sameAddressKey} = itsmAddressModelFactory(rawAddressFormValues);
    if (this.itsmMatchingAddressMap[sameAddressKey]) {
      this.itsmAddressInUseWarning = "ERROR - An address with the same values already exists at this site";
      this.allowedAddress = false;
      this.itsmAddressInUse = true;
      return;
    }

    this.itsmBpIdWarning = "";
    this.allowedAddress = true;
  }

  submit() {
    this.result.selectedTmcAddressId = this.selectedTmcAddressId;
    this.result.changed = this.selectedTmcAddressId !== this.originalTmcAddressId;
    this.result.createdAddress = null;
    this.close();
  }

  clear() {
    this.result.selectedTmcAddressId = this.originalTmcAddressId;
    this.result.changed = false;
    this.result.createdAddress = null;
    this.close();
  }

  @toggleLoading()
  @handleErrors()
  async loadData() {
    // this.enabledIndirectDataModel = await this.appSettingsService.getFeaturePermission(
    //   "enableIndirectDataModel",
    //   this.projectId,
    // );
    const POR662=await this.appSettingsService.getFeaturePermission("POR662_TMC");
    this.loading = true;
    // Set up our defaulting
    const rawValues = this.addressForm.getRawValue();
    Object.entries(this.defaultAddress).forEach(([key, value]) => {
      if (value && rawValues !== value && this.addressForm.controls[key]) { // Extra code to keep the dirty flag correct
        this.addressForm.controls[key].setValue(value);
      }
    });
    const country = await this.projectService.getProjectCountry(this.projectId);
    this.countryId = country.countryId;
    this.addressForm.controls.country.setValue(this.countryId);
    country.geoRegion=="EMEA" && POR662? this.isRegionRequired = false: this.isRegionRequired = true;
    this.placeholders = shippingLabelPlaceholders
      .filter(it =>
        (!it.disabledTargetSystems || !it.disabledTargetSystems[this.targetSystem]));

    this.originalTmcAddressId = this.selectedTmcAddressId;

    this.itsmAddresses = this.itsmAddresses || [];
    this.adjustAddressListHelper(true);

    this.itsmBpIdMap = this.itsmAddresses
      .filter(it => it.businessPartnerId && it.businessPartnerId.trim())
      .reduce((acc, it) => { acc[it.businessPartnerId.trim().toLowerCase()] = it; return acc; }, {});

    this.itsmBuildingNameMap = this.itsmAddresses
      .filter(it => it.itsmBuilding && it.itsmBuilding.trim())
      .reduce((acc, it) => { acc[it.itsmBuilding.trim().toLowerCase()] = it; return acc; }, {});

    this.itsmMatchingAddressMap = this.itsmAddresses
      .filter(it => it.sameAddressKey)
      .reduce(
        (acc, it) => {
          if (!acc[it.sameAddressKey] || !acc[it.sameAddressKey].businessPartnerId) {
            acc[it.sameAddressKey] = it;
          }
          return acc;
        },
        {},
      );

    if (this.allowCreate && !this.itsmSiteId) {
      this.allowCreate = false;
      this.error = new Error("Create is not currently supported unless requested under a specified site");
    }
  }

  ngOnInit() {
    this.loadData();
  }

  constructor(public dialogService: DialogService,
    private appSettingsService: AppSettingsService,
    private projectService: ProjectService) {
    super(dialogService);

    this.result = {
      selectedTmcAddressId: this.selectedTmcAddressId,
      changed: false,
      createdAddress: null,
    };
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
    this.refreshAddressList();
  }

  closeExpandedAddressHelper() {
    this.expandedTmcAddressId = ""; // Close any selections
    this.addressDetail = this.emptyAddress;
  }

  refreshAddressList() {
    if (this.itsmAddresses) {
      this.adjustAddressListHelper(true);
      this.closeExpandedAddressHelper();
    }
  }

  prefilterAddressList() {
    // We pass the full address list for create cases, but need to remove those addresses from being selectable
    this.itsmAddresses = this.itsmAddresses || [];
    this.preFilteredItsmAddresses = this.itsmAddresses.filter((address) => {
      if (this.requireBpId && !address.businessPartnerId) return false;
      if (this.itsmSiteId && address.itsmSiteId !== this.itsmSiteId) return false;
      return true;
    });
  }

  private adjustAddressListHelper(topElementSelected = false) {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.prefilterAddressList();
    this.totalCount = this.preFilteredItsmAddresses.length;
    this.filteredItsmAddresses = this.preFilteredItsmAddresses.filter((address) => {
      if (!address) return false; // Not sure this can occur

      if (this.searchText) {
        const values = [
          address.businessPartnerId,
          address.itsmBuildingId, // Mostly for the pre-select on found matching address
          address.itsmBuilding,
          address.name1,
          address.line1,
          address.city,
          address.postalCode,
        ];

        const searchFailures = SearchHelpers.tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;
        if (searchFailures) return false;
      }

      return true;
    });

    this.maxPages = Math.ceil(this.filteredItsmAddresses.length / (+this.pageSize));
    if (topElementSelected) {
      this.displayedItsmAddresses = [
        ...this.preFilteredItsmAddresses.filter(address => address.id === this.selectedTmcAddressId),
        ...(this.filteredItsmAddresses.slice(start, end).filter(
          it => it.id !== this.selectedTmcAddressId &&
            it.buildingTypes &&
            it.buildingTypes.includes(this.buildingType),
        )),
      ];
    }
  }

  setTmcAddressIdHelper(id) {
    this.selectedTmcAddressId = id;
  }

  setAddress(tmcAddressId) {
    if (tmcAddressId) {
      if (this.preFilteredItsmAddresses && this.preFilteredItsmAddresses.length) {
        const matchingAddress = this.preFilteredItsmAddresses.find(it => it.id === tmcAddressId);
        this.setTmcAddressIdHelper(matchingAddress ? tmcAddressId : "");
      }
    } else this.setTmcAddressIdHelper("");

    this.closeExpandedAddressHelper();
    this.adjustAddressListHelper();
  }

  @toggleLoading()
  @handleErrors()
  async createItsmAddress() {
    const values = this.addressForm.getRawValue();

    this.checkPreExistingAddress(values);
    this.checkBuildingName(values.itsmBuilding);
    this.checkBpId(values.businessPartnerId);

    const addressToExport =
      itsmCreatableAddressModelFactor(values, this.itsmSiteId, this.itsmSiteName, this.requireBpId);
    const errors = this.allowCreate ?
      [
        this.projectId ? null : "TMC Project not found!",
        addressToExport.itsmSiteId ? null : "ITSM Site not found!",
        this.itsmAddressInUseWarning,
        (addressToExport.businessPartnerId || !this.requireBpId) ? null : "BP ID is required",
        this.itsmBpIdWarning,
        this.itsmBuildingNameWarning,
        addressToExport.itsmBuilding ? null : "Building is Required",
        addressToExport.name1 ? null : "Name 1 is required",
        addressToExport.line1 ? null : "Line 1 is required",
        addressToExport.city ? null : "City is required",
        addressToExport.country ? null : "Country is required",
        addressToExport.postalCode ? null : "Postal Code is required",
        addressToExport.region ? null : ( this.isRegionRequired ? "Region is required" : null),
        (addressToExport.customShippingLabel1) ? null : "Shipping Label 1 is required",
        (addressToExport.customShippingLabel2) ? null : "Shipping Label 2 is required",
        (addressToExport.buildingTypes && addressToExport.buildingTypes.length) ? null : "Building Types is required",
      ].filter(it => it).join(". ") :
      "Create is not enabled for this address";

    if (errors) {
      this.error = new Error(errors);
    } else {
      this.error = null;
      this.loading = true;

      const createResults = await this.addressService.createItsmAddress(addressToExport, this.projectId);
      if (createResults && createResults.itsmBuildingId && createResults.id) {
        this.result.selectedTmcAddressId = createResults.id;
        this.result.changed = createResults.id !== this.originalTmcAddressId;
        this.result.createdAddress = createResults;

        this.close();
      } else {
        this.error = new Error("The address returned did not have the required identification information");
      }
    }
  }

  createAddressEnable() {
    this.error = null;
    this.createSelected = true;
  }

  searchAddressEnable() {
    this.error = null;
    if (this.itsmBpIdInUse) {
      this.searchText = this.addressForm.getRawValue().businessPartnerId;
      this.adjustAddressListHelper();
    } else if (this.itsmAddressInUse) {
      const form = this.addressForm.getRawValue();
      this.searchText = form.businessPartnerId || form.itsmBuildingId;
      this.adjustAddressListHelper();
    } else if (this.itsmBuildingNameInUse) {
      this.searchText = this.addressForm.getRawValue().itsmBuilding;
      this.adjustAddressListHelper();
    }

    this.createSelected = false;
  }

  onFocus(parameter) {
    this.currentFocus = parameter;
  }

  addPlaceholder(placeholder) {
    if (this.currentFocus && placeholder) {
      const currentVal = this.addressForm.getRawValue()[this.currentFocus] || "";
      this.addressForm.controls[this.currentFocus].setValue(`${currentVal}${placeholder}`);
    }
  }
}
